import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ALERT_CLASSES, type TaskView } from '@/lib/tasks-board'
import { cn } from '@/utils/cn'

interface TaskCalendarProps {
  /** AAAA-MM */
  month: string
  today: string
  tasks: TaskView[]
  onMonthChange: (month: string) => void
  onOpen: (task: TaskView) => void
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

/** Dias do mês com o preenchimento da semana (null = fora do mês). */
export function monthCells(month: string): Array<string | null> {
  const [y, m] = month.split('-').map(Number)
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay()
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const cells: Array<string | null> = Array(first).fill(null)
  for (let d = 1; d <= days; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`)
  while (cells.length % 7) cells.push(null)
  return cells
}

/** Calendário de prazos: cada tarefa no dia do prazo interno (ou due_date). */
export function TaskCalendar({ month, today, tasks, onMonthChange, onOpen }: TaskCalendarProps) {
  const [y, m] = month.split('-').map(Number)
  const byDay = new Map<string, TaskView[]>()
  for (const t of tasks) {
    if (!t.data_referencia) continue
    byDay.set(t.data_referencia, [...(byDay.get(t.data_referencia) ?? []), t])
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" aria-label="Mês anterior" onClick={() => onMonthChange(shiftMonth(month, -1))}>
          <ChevronLeft className="size-4" />
        </Button>
        <h2 className="min-w-40 text-center font-semibold capitalize">{MONTHS[m - 1]} de {y}</h2>
        <Button variant="outline" size="icon" aria-label="Próximo mês" onClick={() => onMonthChange(shiftMonth(month, 1))}>
          <ChevronRight className="size-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border bg-border text-xs">
        {WEEKDAYS.map((w) => <div key={w} className="bg-muted px-2 py-1 font-medium">{w}</div>)}
        {monthCells(month).map((day, i) => (
          <div key={day ?? `vazio-${i}`} className={cn('min-h-24 bg-background p-1', !day && 'bg-muted/30')}>
            {day && (
              <>
                <div className={cn('mb-1 tabular-nums', day === today && 'font-bold text-primary')}>{Number(day.slice(8))}</div>
                <ul className="flex flex-col gap-0.5">
                  {(byDay.get(day) ?? []).map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => onOpen(t)}
                        title={`${t.title} · ${t.assigned_user_name}`}
                        className={cn('w-full truncate rounded-sm px-1 py-0.5 text-left',
                          t.alerta ? ALERT_CLASSES[t.alerta.cor] : t.etapa === 'CONCLUIDA' ? 'bg-muted text-muted-foreground line-through' : 'bg-secondary')}
                      >
                        {t.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
